import {
  Text,
  GreyGrey10,
  GreyGrey30,
  GreyGrey40,
  GreyGrey60,
  GreyGrey70,
  GreyGrey90,
  Link,
  Primary13SemiBold,
  Primary13Regular,
  Primary20SemiBold,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { Card, Skeleton } from '@sequencehq/core-components'
import { toMoney } from '@sequencehq/utils'
import { Currency } from '@sequencehq/api/utils/commonEnums'
import { RevRecOverviewCustomer } from '../../utils/useRevRecOverviewData'
import { ProgressBar } from 'RevenueRecognition/view/common/ProgressBar'
import { format, formatDateRange } from '@sequencehq/utils/dates'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { OVERVIEW_CARD_HEIGHT } from '../constants'

const createJournalReportsUrl = (
  customerId: string,
  currency: Currency | null,
  dateRange: [Date, Date]
) => {
  const params = new URLSearchParams()

  params.set('customerId', customerId)

  if (currency) {
    params.set('currency', currency)
  }

  if (dateRange) {
    params.set('startDate', format(dateRange[0], 'yyyy-MM-dd'))
    params.set('endDate', format(dateRange[1], 'yyyy-MM-dd'))
  }

  return `/revenue-recognition/journal-reports?${params.toString()}`
}

interface OverviewCustomersProps {
  isLoading: boolean
  customers: RevRecOverviewCustomer[]
  currency: Currency
  dateRange: [Date, Date]
}

export const OverviewCustomers = ({
  isLoading,
  currency,
  dateRange,
  customers
}: OverviewCustomersProps) => {
  if (isLoading) {
    return (
      <Skeleton
        width="400px"
        height={OVERVIEW_CARD_HEIGHT}
        borderRadius="8px"
      />
    )
  }

  return (
    <Card
      height={OVERVIEW_CARD_HEIGHT}
      width="400px"
      data-testid="revrec.overview.customersSection"
      borderRadius="8px"
      border={`1px solid ${GreyGrey40}`}
      boxShadow="0px 0px 1px rgba(0, 0, 0, 0.02), 0px 1px 1px rgba(0, 0, 0, 0.05)"
      padding="0"
      overflow="hidden"
      backgroundColor="white"
    >
      <Flex
        padding="16px"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Flex flexDirection="column" gap="8px" minWidth="200px">
          <Text {...Primary13Regular} fontWeight="semibold" color={GreyGrey60}>
            Customers
          </Text>
          <Text {...Primary20SemiBold} color={GreyGrey90}>
            Top customers
          </Text>
          <Text {...Primary13Regular} color={GreyGrey60}>
            {formatDateRange({
              from: dateRange[0],
              to: dateRange[1],
              separator: ' - '
            })}
          </Text>
        </Flex>
      </Flex>

      <Box px="16px" pb="16px">
        <Grid
          templateColumns="1fr auto auto"
          pb={2}
          borderBottom={`1px solid ${GreyGrey30}`}
        >
          <Text {...Primary13Regular} fontWeight="semibold" color={GreyGrey60}>
            Customer
          </Text>
          <Text
            {...Primary13Regular}
            fontWeight="semibold"
            color={GreyGrey60}
            textAlign="center"
            minWidth="60px"
          >
            Recognized
          </Text>
        </Grid>

        {customers.map((customer, index) => (
          <Link.Unstyled
            key={customer.customerId}
            to={createJournalReportsUrl(
              customer.customerId,
              currency,
              dateRange
            )}
            style={{
              textDecoration: 'none',
              color: 'inherit',
              display: 'block',
              backgroundColor: 'transparent'
            }}
          >
            <Grid
              templateColumns="1fr auto auto"
              borderBottom={
                index < customers.length - 1
                  ? `1px solid ${GreyGrey30}`
                  : undefined
              }
              _hover={{
                backgroundColor: GreyGrey10
              }}
              height="32px"
              gap="4px"
            >
              <Flex alignItems="center" gap={0} overflow="auto">
                <CustomerPreviewCardPill
                  customerId={customer.customerId}
                  variant="ghost"
                />
              </Flex>

              <Flex alignItems="center" justifyContent="center" minWidth="60px">
                <Text {...Primary13Regular} color={GreyGrey70}>
                  {customer.percentageOfTotal.toFixed(0)}%
                </Text>
                <Box ml={2} width="40px">
                  <ProgressBar percentage={customer.percentageOfTotal} />
                </Box>
              </Flex>

              <Flex
                alignItems="center"
                justifyContent="flex-end"
                minWidth="100px"
              >
                <Text
                  {...Primary13SemiBold}
                  color={GreyGrey70}
                  fontWeight="600"
                >
                  {toMoney({
                    currency,
                    value: customer.recognizedRevenue.toString()
                  })}
                </Text>
              </Flex>
            </Grid>
          </Link.Unstyled>
        ))}
      </Box>
    </Card>
  )
}
